import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'

import { ChevronRightIcon, YesIcon } from '@dfds-frontend/navigator-icons/icons'

import { useVehicles } from '../../Hooks/vehicles/useVehicles'

import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { handleListNavigation } from '../../Utils/list-keyboard'
import { VehicleIcon } from './VehicleIcon'

interface Props {
  onSelectCategory: (categoryCode: string) => void
  selectedCategoryCode: string | undefined
}

const Categories: React.FC<Props> = ({ onSelectCategory, selectedCategoryCode }) => {
  const { t } = useTranslation()
  const { selectedVehicle, vehicleApiData } = useVehicles()

  const categories = vehicleApiData.vehicleTypes?.map((vehicleType) => vehicleType.categoryCode)
  const categoriesUnique = [...new Set(categories)]

  const {
    state: { residentVehicleChecked },
  } = useTravelSearchContext()

  // Roving tabindex: the list is a single tab stop, arrow keys move within it.
  const isFootPassengerSelected = selectedVehicle?.code === 'NCAR'

  return !selectedCategoryCode ? (
    <ul
      aria-label={t('VEHICLE-CATEGORIES')}
      className="m-0 list-none border-0 p-0 pl-0"
      data-e2e="vehicle-categories"
      data-list
      data-travel-search-child
    >
      {categoriesUnique.map((category, index) => (
        <li className="m-0 list-none" data-e2e={`vehicle-category-${category}`} key={category}>
          <button
            className={clsx(
              'flex w-full cursor-pointer items-center gap-semantic-global-md border-0 border-solid bg-semantic-global-background-white px-semantic-global-md pr-semantic-global-md text-left focus-visible:z-10 focus-visible:outline-2 focus-visible:-outline-offset-2! focus-visible:outline-semantic-global-primary-default lg:hover:bg-semantic-global-primary-lightest',
              'min-h-[90px]',
              index === categoriesUnique.length - 1 && vehicleApiData.isVehicleMandatory
                ? 'border-b-0'
                : 'border-b border-b-semantic-global-border-light'
            )}
            data-list-option
            onClick={() => {
              onSelectCategory(category)
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') {
                event.preventDefault()
                onSelectCategory(category)
                return
              }
              handleListNavigation(event)
            }}
            tabIndex={!isFootPassengerSelected && index === 0 ? 0 : -1}
            type="button"
          >
            <div className="flex h-full min-w-[46px] max-w-[46px] items-center text-semantic-global-text-default">
              <VehicleIcon code={category} size="lg" />
            </div>
            <span className="cursor-pointer text-base font-medium text-semantic-global-text-default">
              {t(`VEHICLE-CATEGORY-${category}`)}
            </span>
            <div className="ml-auto text-semantic-global-text-default">
              <ChevronRightIcon size="md" />
            </div>
          </button>
        </li>
      ))}
      {!vehicleApiData.isVehicleMandatory ? (
        <li className="m-0 list-none" data-e2e="vehicle-NCAR" key="walkon">
          <button
            aria-pressed={selectedVehicle?.code === 'NCAR'}
            className={clsx(
              'flex w-full items-center gap-semantic-global-md border-0 border-b border-solid border-b-semantic-global-border-light px-semantic-global-md py-semantic-global-md pr-semantic-global-md text-left focus-visible:z-10 focus-visible:outline-2 focus-visible:-outline-offset-2! focus-visible:outline-semantic-global-primary-default lg:h-[90px] lg:py-0',
              'min-h-[90px]',
              residentVehicleChecked
                ? 'cursor-not-allowed bg-semantic-global-disabled-light'
                : selectedVehicle?.code === 'NCAR'
                  ? 'cursor-pointer bg-semantic-form-controls-background-ghost-pressed lg:hover:bg-semantic-global-primary-lightest'
                  : 'cursor-pointer bg-semantic-global-background-white lg:hover:bg-semantic-global-primary-lightest'
            )}
            data-list-option
            disabled={residentVehicleChecked}
            onClick={() => {
              if (residentVehicleChecked) return
              onSelectCategory('NCAR')
            }}
            onKeyDown={handleListNavigation}
            tabIndex={isFootPassengerSelected ? 0 : -1}
            type="button"
          >
            <div
              className={clsx(
                'flex h-full min-w-[46px] max-w-[46px] items-center',
                residentVehicleChecked ? 'text-semantic-global-disabled-default' : 'text-semantic-global-text-default'
              )}
            >
              <VehicleIcon code="NCAR" size="lg" />
            </div>
            <div className="flex flex-col gap-1">
              <span
                className={clsx(
                  'text-base font-medium ',
                  residentVehicleChecked
                    ? 'cursor-not-allowed text-semantic-global-disabled-default'
                    : 'cursor-pointer text-semantic-global-primary-darkest'
                )}
              >
                {t('VEHICLE-CATEGORY-FOOTPASSENGER')}
              </span>
              {residentVehicleChecked ? (
                <div className="text-semantic-global-primary-darkest">{t('RESIDENT-VEHICLE-REQUIRES-VEHICLE')}</div>
              ) : null}
            </div>
            {selectedVehicle?.code === 'NCAR' ? (
              <div className="ml-auto text-semantic-global-text-default">
                <YesIcon size="md" />
              </div>
            ) : null}
          </button>
        </li>
      ) : null}
    </ul>
  ) : null
}

export default Categories
